// Canonical location picklists — cities, states/provinces, countries.
//
// The DealCloud mirror stores locations as FULL NAMES ("Texas", not "TX";
// "United States", not "USA"), so every list here follows that convention.
// These lists back <datalist> suggestions on the location inputs (company /
// contact detail rows, the new-company drawer, deal filters) so hand-typed
// values converge on one spelling instead of fragmenting into TX/Tex./Texas.
//
// Suggestions, not enforcement: inputs still accept free text because the
// data legitimately contains international regions (French departments,
// Nigerian states, ...) and towns not on the city list.

// US states + DC + territories, then Canadian provinces/territories —
// the two countries that make up >99% of the data.
const GEO_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado',
  'Connecticut', 'Delaware', 'District of Columbia', 'Florida', 'Georgia',
  'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky',
  'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota',
  'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire',
  'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota',
  'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania', 'Puerto Rico', 'Rhode Island',
  'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont',
  'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming',
  // Canada
  'Alberta', 'British Columbia', 'Manitoba', 'New Brunswick',
  'Newfoundland and Labrador', 'Northwest Territories', 'Nova Scotia',
  'Nunavut', 'Ontario', 'Prince Edward Island', 'Quebec', 'Saskatchewan',
  'Yukon',
];

// Postal codes for GEO_STATES. The data never stores them, but people type
// them ("GA, AL, TN"), so pickers resolve a code to the stored full name.
const GEO_STATE_CODES = {
  'Alabama': 'AL', 'Alaska': 'AK', 'Arizona': 'AZ', 'Arkansas': 'AR',
  'California': 'CA', 'Colorado': 'CO', 'Connecticut': 'CT', 'Delaware': 'DE',
  'District of Columbia': 'DC', 'Florida': 'FL', 'Georgia': 'GA', 'Hawaii': 'HI',
  'Idaho': 'ID', 'Illinois': 'IL', 'Indiana': 'IN', 'Iowa': 'IA', 'Kansas': 'KS',
  'Kentucky': 'KY', 'Louisiana': 'LA', 'Maine': 'ME', 'Maryland': 'MD',
  'Massachusetts': 'MA', 'Michigan': 'MI', 'Minnesota': 'MN', 'Mississippi': 'MS',
  'Missouri': 'MO', 'Montana': 'MT', 'Nebraska': 'NE', 'Nevada': 'NV',
  'New Hampshire': 'NH', 'New Jersey': 'NJ', 'New Mexico': 'NM', 'New York': 'NY',
  'North Carolina': 'NC', 'North Dakota': 'ND', 'Ohio': 'OH', 'Oklahoma': 'OK',
  'Oregon': 'OR', 'Pennsylvania': 'PA', 'Puerto Rico': 'PR', 'Rhode Island': 'RI',
  'South Carolina': 'SC', 'South Dakota': 'SD', 'Tennessee': 'TN', 'Texas': 'TX',
  'Utah': 'UT', 'Vermont': 'VT', 'Virginia': 'VA', 'Washington': 'WA',
  'West Virginia': 'WV', 'Wisconsin': 'WI', 'Wyoming': 'WY',
  'Alberta': 'AB', 'British Columbia': 'BC', 'Manitoba': 'MB', 'New Brunswick': 'NB',
  'Newfoundland and Labrador': 'NL', 'Northwest Territories': 'NT',
  'Nova Scotia': 'NS', 'Nunavut': 'NU', 'Ontario': 'ON',
  'Prince Edward Island': 'PE', 'Quebec': 'QC', 'Saskatchewan': 'SK', 'Yukon': 'YT',
};

// Most-used first (matches the data: US ~310k rows, Canada ~1.2k, UK ~160),
// then the rest alphabetically. Datalists show options in DOM order while
// the input is empty, so the common picks surface on focus.
const GEO_COUNTRIES = [
  'United States', 'Canada', 'United Kingdom', 'Australia',
  'Afghanistan', 'Albania', 'Algeria', 'Andorra', 'Angola', 'Argentina',
  'Armenia', 'Austria', 'Azerbaijan', 'Bahamas', 'Bahrain', 'Bangladesh',
  'Barbados', 'Belarus', 'Belgium', 'Belize', 'Benin', 'Bermuda', 'Bhutan',
  'Bolivia', 'Bosnia and Herzegovina', 'Botswana', 'Brazil', 'Brunei',
  'Bulgaria', 'Burkina Faso', 'Burundi', 'Cambodia', 'Cameroon',
  'Cape Verde', 'Cayman Islands', 'Central African Republic', 'Chad',
  'Chile', 'China', 'Colombia', 'Comoros', 'Costa Rica', 'Croatia', 'Cuba',
  'Cyprus', 'Czech Republic', 'Democratic Republic of the Congo', 'Denmark',
  'Djibouti', 'Dominica', 'Dominican Republic', 'Ecuador', 'Egypt',
  'El Salvador', 'Equatorial Guinea', 'Eritrea', 'Estonia', 'Eswatini',
  'Ethiopia', 'Fiji', 'Finland', 'France', 'Gabon', 'Gambia', 'Georgia',
  'Germany', 'Ghana', 'Greece', 'Grenada', 'Guatemala', 'Guinea',
  'Guinea-Bissau', 'Guyana', 'Haiti', 'Honduras', 'Hong Kong', 'Hungary',
  'Iceland', 'India', 'Indonesia', 'Iran', 'Iraq', 'Ireland', 'Israel',
  'Italy', 'Ivory Coast', 'Jamaica', 'Japan', 'Jordan', 'Kazakhstan',
  'Kenya', 'Kiribati', 'Kosovo', 'Kuwait', 'Kyrgyzstan', 'Laos', 'Latvia',
  'Lebanon', 'Lesotho', 'Liberia', 'Libya', 'Liechtenstein', 'Lithuania',
  'Luxembourg', 'Madagascar', 'Malawi', 'Malaysia', 'Maldives', 'Mali',
  'Malta', 'Marshall Islands', 'Mauritania', 'Mauritius', 'Mexico',
  'Micronesia', 'Moldova', 'Monaco', 'Mongolia', 'Montenegro', 'Morocco',
  'Mozambique', 'Myanmar', 'Namibia', 'Nauru', 'Nepal', 'Netherlands',
  'New Zealand', 'Nicaragua', 'Niger', 'Nigeria', 'North Korea',
  'North Macedonia', 'Norway', 'Oman', 'Pakistan', 'Palau', 'Panama',
  'Papua New Guinea', 'Paraguay', 'Peru', 'Philippines', 'Poland',
  'Portugal', 'Qatar', 'Republic of the Congo', 'Romania', 'Russia',
  'Rwanda', 'Saint Kitts and Nevis', 'Saint Lucia',
  'Saint Vincent and the Grenadines', 'Samoa', 'San Marino',
  'Sao Tome and Principe', 'Saudi Arabia', 'Senegal', 'Serbia',
  'Seychelles', 'Sierra Leone', 'Singapore', 'Slovakia', 'Slovenia',
  'Solomon Islands', 'Somalia', 'South Africa', 'South Korea',
  'South Sudan', 'Spain', 'Sri Lanka', 'Sudan', 'Suriname', 'Sweden',
  'Switzerland', 'Syria', 'Taiwan', 'Tajikistan', 'Tanzania', 'Thailand',
  'Timor-Leste', 'Togo', 'Tonga', 'Trinidad and Tobago', 'Tunisia',
  'Turkey', 'Turkmenistan', 'Tuvalu', 'Uganda', 'Ukraine',
  'United Arab Emirates', 'Uruguay', 'Uzbekistan', 'Vanuatu',
  'Vatican City', 'Venezuela', 'Vietnam', 'Yemen', 'Zambia', 'Zimbabwe',
];

// Major US + Canadian cities. Far from exhaustive (the mirror has ~13k
// distinct cities) — this is about nailing the spelling/casing of the
// common ones ("St. Louis", "Winston-Salem", "Fort Worth").
const GEO_CITIES = [
  'Akron', 'Albany', 'Albuquerque', 'Alexandria', 'Allentown', 'Amarillo',
  'Anaheim', 'Anchorage', 'Ann Arbor', 'Arlington', 'Asheville', 'Atlanta',
  'Augusta', 'Aurora', 'Austin', 'Bakersfield', 'Baltimore', 'Baton Rouge',
  'Beaumont', 'Bellevue', 'Bentonville', 'Berkeley', 'Bethesda',
  'Birmingham', 'Boca Raton', 'Boise', 'Boston', 'Boulder', 'Bridgeport',
  'Brooklyn', 'Buffalo', 'Burlington', 'Cambridge', 'Cape Coral',
  'Carlsbad', 'Cary', 'Cedar Rapids', 'Chandler', 'Charleston',
  'Charlotte', 'Chattanooga', 'Chesapeake', 'Chicago', 'Cincinnati',
  'Clearwater', 'Cleveland', 'Colorado Springs', 'Columbia', 'Columbus',
  'Concord', 'Corpus Christi', 'Dallas', 'Dayton', 'Daytona Beach',
  'Denver', 'Des Moines', 'Detroit', 'Duluth', 'Durham', 'El Paso',
  'Erie', 'Eugene', 'Evansville', 'Fargo', 'Fayetteville', 'Flint',
  'Fort Collins', 'Fort Lauderdale', 'Fort Myers', 'Fort Wayne',
  'Fort Worth', 'Fresno', 'Frisco', 'Gainesville', 'Grand Rapids',
  'Green Bay', 'Greensboro', 'Greenville', 'Harrisburg', 'Hartford',
  'Henderson', 'Honolulu', 'Houston', 'Huntington Beach', 'Huntsville',
  'Indianapolis', 'Irvine', 'Irving', 'Jacksonville', 'Jersey City',
  'Kalamazoo', 'Kansas City', 'Knoxville', 'Lakeland', 'Lancaster',
  'Lansing', 'Laredo', 'Las Vegas', 'Lexington', 'Lincoln', 'Little Rock',
  'Long Beach', 'Los Angeles', 'Louisville', 'Lubbock', 'Madison',
  'Manchester', 'McAllen', 'Memphis', 'Mesa', 'Miami', 'Milwaukee',
  'Minneapolis', 'Mobile', 'Modesto', 'Montgomery', 'Naperville',
  'Naples', 'Nashville', 'New Haven', 'New Orleans', 'New York',
  'Newark', 'Newport Beach', 'Norfolk', 'Oakland', 'Oklahoma City',
  'Omaha', 'Orlando', 'Overland Park', 'Oxnard', 'Palm Beach',
  'Pasadena', 'Pensacola', 'Peoria', 'Philadelphia', 'Phoenix',
  'Pittsburgh', 'Plano', 'Portland', 'Providence', 'Provo', 'Raleigh',
  'Reno', 'Richmond', 'Riverside', 'Rochester', 'Rockford', 'Sacramento',
  'Salem', 'Salt Lake City', 'San Antonio', 'San Diego', 'San Francisco',
  'San Jose', 'Santa Barbara', 'Santa Fe', 'Santa Monica', 'Santa Rosa',
  'Sarasota', 'Savannah', 'Scottsdale', 'Seattle', 'Shreveport',
  'Sioux Falls', 'South Bend', 'Spokane', 'Springfield', 'St. Louis',
  'St. Paul', 'St. Petersburg', 'Stamford', 'Stockton', 'Syracuse',
  'Tacoma', 'Tallahassee', 'Tampa', 'Tempe', 'Toledo', 'Topeka',
  'Trenton', 'Tucson', 'Tulsa', 'Virginia Beach', 'Waco', 'Washington',
  'West Palm Beach', 'Wichita', 'Wilmington', 'Winston-Salem', 'Worcester',
  // Canada
  'Brampton', 'Calgary', 'Edmonton', 'Halifax', 'Hamilton', 'Kitchener',
  'London', 'Mississauga', 'Montreal', 'Ottawa', 'Quebec City', 'Regina',
  'Saskatoon', 'Surrey', 'Toronto', 'Vancouver', 'Victoria', 'Windsor',
  'Winnipeg',
];

// Shared <datalist> elements, installed once at import time (same pattern
// as primitives.jsx attaching its shimmer keyframes). Any input can then
// opt in with list="geo-state-list" etc. — no per-callsite markup.
const GEO_LIST_IDS = {
  city: 'geo-city-list',
  state: 'geo-state-list',
  country: 'geo-country-list',
};

const installGeoDatalist = (id, values) => {
  if (document.getElementById(id)) return;
  const dl = document.createElement('datalist');
  dl.id = id;
  dl.innerHTML = values.map((v) => `<option value="${v}"></option>`).join('');
  document.body.appendChild(dl);
};

installGeoDatalist(GEO_LIST_IDS.city, GEO_CITIES);
installGeoDatalist(GEO_LIST_IDS.state, GEO_STATES);
installGeoDatalist(GEO_LIST_IDS.country, GEO_COUNTRIES);

window.GEO = {
  CITIES: GEO_CITIES,
  STATES: GEO_STATES,
  STATE_CODES: GEO_STATE_CODES,
  COUNTRIES: GEO_COUNTRIES,
  LIST_IDS: GEO_LIST_IDS,
};
